import { type CanvasRetrieve } from '@openapi'
import { componentMeta } from '@canvas-builder/constants'
import { filterTreeBy } from '@canvas-builder/util/tree'
import {
  CanvasBuilderMode,
  ComponentTypes,
  type ComponentNode,
  type ComponentNodeVisible,
} from '@canvas-builder/types/canvas'
import { isTextComponentEmpty } from '@canvas-builder/util/canvas.util'
import {
  HIDE_IF_EMPTY_COMPONENT_ID_TAG_PREFIX,
  HIDE_IF_EMPTY_COMPONENT_TRACKING_ID_TAG_PREFIX,
} from '@lib/constants/conventions.const'
import useCanvas from '@lib/apps/canvas-builder/composables/useCanvas'
import { type CollectionPlayer } from '@collection-player/types/collection-player.type.ts'

const getComponentDefaultVisibility = (type: ComponentNode['type'], mode?: keyof ComponentNodeVisible) => {
  if (!mode || mode !== 'pdf') {
    return true
  }

  return componentMeta?.[type]?.defaultPrintable ?? true
}

const getVisibleCanvasContent = (data: ComponentNode[], mode?: keyof ComponentNodeVisible) => {
  if (!mode) return data

  return filterTreeBy(data, (node) => {
    let isVisible: boolean = node?.visible?.[mode] ?? getComponentDefaultVisibility(node.type, mode)

    // ignore pdf visibility if web is disabled
    if (mode === 'pdf' && isVisible) {
      const isVisibleWeb: boolean = node?.visible?.web ?? getComponentDefaultVisibility(node.type, 'web')

      if (!isVisibleWeb) {
        isVisible = false
      }
    }

    // Ignore empty Collection Player in Presentation Mode AND DSR (except for data_accessor components)
    if (['presentation_mode', 'web'].includes(mode) && isVisible && node.type === ComponentTypes.CollectionPlayer) {
      const collectionData = node.data as CollectionPlayer
      const hasSlides = collectionData?.groups?.some((group) => group.slides?.length > 0)
      const hasDataAccessor = !!(collectionData as any)?.data_accessor
      if (!hasSlides && !hasDataAccessor) {
        isVisible = false
      }
    }

    if (isVisible && node.children?.length) {
      node.children = getVisibleCanvasContent(node.children, mode)

      if (node.children.length === 0) {
        return false
      }
    }

    return isVisible
  })
}

const getVisibleKey = (): keyof ComponentNodeVisible | undefined => {
  const { displayMode, mode, isEditMode } = useCanvas()

  if (displayMode.value === 'print') {
    return 'pdf'
  } else if (mode.value === 'sharing') {
    return 'web'
  } else if ((mode.value === CanvasBuilderMode.ADMIN || mode.value === CanvasBuilderMode.IMPACT) && !isEditMode.value) {
    return 'presentation_mode'
  }

  return undefined
}

const shouldRenderComponentBasedOnHideIfEmptyIdTag = (tag: string, sectionId?: CanvasRetrieve['id']) => {
  const { componentNodesById, sectionComponentNodesBySectionId, sectionComponentOverrides } = useCanvas()

  const componentIdToCheckIfEmpty = tag?.split(':')?.pop()?.trim()
  if (!componentIdToCheckIfEmpty) return true
  let component
  if (sectionId) {
    component = sectionComponentNodesBySectionId.value[sectionId][componentIdToCheckIfEmpty]
  } else {
    component = componentNodesById.value[componentIdToCheckIfEmpty]
  }

  return !(
    component.type === ComponentTypes.Text &&
    isTextComponentEmpty(component as ComponentNode, sectionComponentOverrides.value, sectionId)
  )
}

const shouldRenderComponentBasedOnHideIfEmptyTrackingIdTag = (tag: string, sectionId?: CanvasRetrieve['id']) => {
  const { componentNodesById, sectionComponentNodesBySectionId, sectionComponentOverrides } = useCanvas()

  const componentTrackingIdToCheckIfEmpty = tag?.split(':')?.pop()?.trim()
  if (!componentTrackingIdToCheckIfEmpty) return true
  let component

  if (sectionId) {
    const sectionComponents = Object.values(sectionComponentNodesBySectionId.value[sectionId])
    component = sectionComponents?.find((i) => i.tracking_id?.trim() === componentTrackingIdToCheckIfEmpty)
  } else {
    const components = Object.values(componentNodesById.value)
    component = components?.find((i) => i.tracking_id?.trim() === componentTrackingIdToCheckIfEmpty)
  }

  return !(
    component?.type === ComponentTypes.Text &&
    isTextComponentEmpty(component as ComponentNode, sectionComponentOverrides.value, sectionId)
  )
}

const shouldRenderComponentWrapper = (c: ComponentNode, sectionId?: CanvasRetrieve['id']) => {
  const { sectionComponentOverrides, isEditMode } = useCanvas()

  if (isEditMode.value) return true

  // Check for empty content of Text component
  if (c.type === ComponentTypes.Text && isTextComponentEmpty(c, sectionComponentOverrides.value, sectionId))
    return false

  // Check for tag-driven-visibility
  if (!c.tags?.length) return true

  const hideIfEmptyIdTag = c.tags.find((item) => item.includes(HIDE_IF_EMPTY_COMPONENT_ID_TAG_PREFIX))
  const hideIfEmptyTrackingIdTag = c.tags.find((item) => item.includes(HIDE_IF_EMPTY_COMPONENT_TRACKING_ID_TAG_PREFIX))

  if (hideIfEmptyIdTag) return shouldRenderComponentBasedOnHideIfEmptyIdTag(hideIfEmptyIdTag, sectionId)
  if (hideIfEmptyTrackingIdTag)
    return shouldRenderComponentBasedOnHideIfEmptyTrackingIdTag(hideIfEmptyTrackingIdTag, sectionId)
}

export default function useCanvasVisibility() {
  return {
    getComponentDefaultVisibility,
    getVisibleCanvasContent,
    getVisibleKey,
    shouldRenderComponentWrapper,
  }
}
